import { JSX } from "react";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { extractParts, ellipsify } from "@app/resource-filters/common/helpers";
import { RootIcon } from "@app/resource-filters/common/RootIcon";
import { SegmentSeparated } from "@app/resource-filters/common/SegmentSeparated";
import { SegmentSeparator } from "@app/resource-filters/common/SegmentSeparator";

/** Renders a set of namespace paths as a single combined string. */
function CombinedNamespacePaths(props: { disableRoot?: boolean, namespacePaths: string[] }): JSX.Element {
  const { namespacePaths, disableRoot } = props;

  if (!namespacePaths || namespacePaths.length === 0) {
    return null;
  }

  // Paths must all begin with the same main portion of their namespace path. Validate.
  let validNamespaces = true;

  // Could this just have been a .split("§"), with a loop over the result to render each piece?
  let lastParts = extractParts(namespacePaths[0]);
  for (const namespacePath of namespacePaths) {
    const parts = extractParts(namespacePath);

    if (lastParts.root !== parts.root || lastParts.beginning !== parts.beginning) {
      validNamespaces = false;
      break;
    }

    lastParts = parts;
  }

  if (!validNamespaces) {
    throw new Error("Cannot render a filter group where the children have differing roots.");
  }

  const commaSeparatedEndings = namespacePaths.reduce((acc, namespacePath) => {
    const { end } = extractParts(namespacePath);
    return acc === "" ? end : `${acc}, ${end}`;
  }, "");

  const ellipsifiedText = ellipsify(commaSeparatedEndings);

  const renderedPath = (
    <>
      {!disableRoot && <RootIcon root={lastParts.root} />}
      {lastParts.beginning !== "\u00A7" && <SegmentSeparated text={ellipsify(lastParts.beginning)} />}
      <SegmentSeparator />
      <SegmentSeparated text={ellipsify(commaSeparatedEndings)} />
    </>
  );

  return (
    ellipsifiedText !== commaSeparatedEndings ?
      (
        <TooltipWrapper text={commaSeparatedEndings} position="bottom">
          {renderedPath}
        </TooltipWrapper>
      )
      : renderedPath
  );
}

export { CombinedNamespacePaths };
